import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CollectionReference, DocumentData, Firestore, QuerySnapshot, addDoc, collection, doc, docData, getDoc, getDocs, query, updateDoc, where } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Auth, signInWithEmailAndPassword, signOut } from '@angular/fire/auth';

import { finalize } from 'rxjs/operators'
import { MatDialog } from '@angular/material/dialog';
import { OrganisationModalComponent } from '../../modals/organisation-modal/organisation-modal.component';
import { SharedService } from '../../../../shared/shared.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { firstValueFrom } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class LogoutService {
	usersCollection: CollectionReference<DocumentData>;
	organisationCollection: CollectionReference<DocumentData>;
	orgId: any;
	numRegex = /^\d+$/;

	constructor(
		private firestore: Firestore,
		private auth: Auth,
		private dialog: MatDialog,
		private sharedService: SharedService,
		private snackbar: MatSnackBar,
		private functions: AngularFireFunctions
	) {
		this.usersCollection = collection(this.firestore, "users");
		this.organisationCollection = collection(this.firestore, "organisations");
	}

	async login(userName: any, password: any, isEmailFormatted: boolean) {
		userName = String(userName);
		if (isEmailFormatted) {
			try {
				// Firestore user reads require authentication. Authenticate first, then
				// resolve the profile using the Firebase Auth UID.
				const userCredential = await signInWithEmailAndPassword(this.auth, userName, password);
				const userDocument = await getDoc(doc(this.firestore, `users/${userCredential.user.uid}`));

				if (!userDocument.exists()) {
					await signOut(this.auth);
					return this.returnLoginStatus('failed', 'Your User Name or Password was incorrect');
				}

				const userData: any = {
					...userDocument.data(),
					id: userDocument.id
				};

				if (userData?.organisationId) {
					const documentRef = doc(this.firestore, `organisations/${userData.organisationId}`);
					const orgDocDataObs = docData(documentRef, { idField: 'id' });
					const orgDocData: any = await this.sharedService.getObservableRes(orgDocDataObs);
					if (!orgDocData.active) {
						await signOut(this.auth);
						return this.returnLoginStatus('failed', 'Sorry, your organization does not have access to Ignite Humans.');
					}
				}

				if (!userData?.status) {
					await signOut(this.auth);
					return this.returnLoginStatus('failed', 'Please ask your organization admin to allow your login!');
				}

				this.saveDataInLocalStorage(userData);
				return this.returnLoginStatus('success', 'Login successful!', userData);
			} catch (err) {
				return this.returnLoginStatus('failed', 'Your User Name or Password was incorrect');
			}
		} else {
			const userDetails: any = await this.getUserDataByCode(userName.toLowerCase());
			// console.log('userDetails',userDetails)
			if (!userDetails) {
				return this.returnLoginStatus('failed', 'Your User Name or Password was incorrect!');
			}

			//Single user exist in single Organisation
			else if (userDetails.length === 1) {
				let userData: any = userDetails[0];
				if (userData?.organisationId) {
					const documentRef = doc(this.firestore, `organisations/${userData.organisationId}`);
					const orgDocDataObs = docData(documentRef, { idField: 'id' });
					const orgDocData: any = await this.sharedService.getObservableRes(orgDocDataObs);
					if (!orgDocData.active) {
						return this.returnLoginStatus('failed', 'Sorry, your organization does not have access to Ignite Humans.');
					}
				}

				if (!userData?.status) {
					return this.returnLoginStatus('failed', 'Please ask your organization admin to allow your login!');
				}

				try {
					const userEmail = userData.email;
					const userCredential = await signInWithEmailAndPassword(this.auth, userEmail, password);
					// const user = userCredential.user;
					this.saveDataInLocalStorage(userData);
					return this.returnLoginStatus('success', 'Login successful!', userData);
				} catch (err) {
					return this.returnLoginStatus('failed', 'Your User Name or Password was incorrect!');
				}
			}

			//Single user exists in multiple organisations
			else if (userDetails.length > 1) {
				let orgIds = [];
				for (let i = 0; i < userDetails.length; i++) {
					orgIds.push(userDetails[i].organisationId)
				}
				await this.openOrgModal(orgIds, userDetails, password);
			}
		}
	}

	async openOrgModal(orgIds: any, userDetails: any, password: string) {
		// console.log(userDetails);
		let data: any;
		const dialogRef = this.dialog.open(OrganisationModalComponent, {
			maxWidth: '50vw',
			minWidth: 'fit-content',
			data: {
				orgIds
			}
		});

		dialogRef.afterClosed().subscribe(async result => {
			console.log(`Dialog result: ${result}`);
			for (let i = 0; i < userDetails.length; i++) {
				if (result === userDetails[i].organisationId) {
					data = userDetails[i];
					break;
				}
			}

			try {
				const userEmail = data.email;
				const userCredential = await signInWithEmailAndPassword(this.auth, userEmail, password);
				const user = userCredential.user;
				// console.log(`User: ${user}`, user);
				this.saveDataInLocalStorage(data);
				window.location.href = this.getPostLoginUrl(data);
			} catch (err) {
				this.snackbar.open('Your User Name or Password was incorrect!', "OK");
			}
		});
	}

	async doesUserExistInCollection(userName: any) {
		const userQuery = query(this.usersCollection, where("email", "==", userName));
		const userQuerySnap = await getDocs(userQuery);

		if (userQuerySnap.size >= 1) {
			return { userData: { ...userQuerySnap.docs[0].data(), id: userQuerySnap.docs[0].id }, doesExist: true };
		}

		return { doesExist: false };
	}


	async getUserDataByCode(empCode: any) {
		try {
			const getUserByEmpCode = this.functions.httpsCallable('user-getUserByEmpCode');
			const users: any = await firstValueFrom(getUserByEmpCode({ empCode }));
			if (users && users.length >= 1) {
				const userData = users.map((u: any) => ({
					...u,
					doesExist: true
				}));
				console.log('DEBUG: Found user data:', userData);
				return userData;
			}
			console.log('DEBUG: No user found, returning false');
			return false;
		} catch (err) {
			console.error('DEBUG: Error querying user by empCode via Cloud Function:', err);
			return false;
		}
	}

	async getUserByEmail(userName: any) {
		const q = query(this.usersCollection, where("email", "==", userName));
		const querySnapshot = await getDocs(q);
		const users: any = [];
		querySnapshot.forEach((doc) => {
			users.push({ id: doc.id, ...doc.data() });
		});
		// console.log(users)
		return users;
	}

	async getOrganizationData(organizationId: any) {
		let org: any = [];
		// console.log(organizationId);
		try {
			for (let i = 0; i < organizationId.orgIds.length; i++) {
				let orgData = docData(doc(this.firestore, `organisations/${organizationId.orgIds[i]}`));
				let data: any = await this.sharedService.getObservableRes(orgData);
				org.push({ id: organizationId.orgIds[i], ...data });
				console.log(org)
			}
			return org;
		} catch (err) {
			console.log('Unable to get organization data', err);
		}
	}

	async updateUser(id: any, data: any) {
		const categRef = doc(this.usersCollection, id);
		await updateDoc(categRef, data);
	}

	async hashPassword(algorithm: string, password: string) {
		return await crypto.subtle.digest(algorithm, new TextEncoder().encode(password));
	}

	base64Encode(buff: any) {
		return btoa(new Uint8Array(buff).reduce((s, b) => s + String.fromCharCode(b), ''));
	}

	returnLoginStatus(status: string, msg: string, userData: any = null) {
		return {
			status,
			msg,
			userData
		}
	}

	getPostLoginUrl(userData: any): string {
		// Bypass mandatory password change redirect
		// const isImpersonating = localStorage.getItem('isImpersonating') === 'true';
		// return userData?.mustChangePassword === true && !isImpersonating
		// 	? '/change-password?required=true'
		// 	: '/';
		return '/';
	}

	setUserSession(userData: any) {
		localStorage.setItem("userId", userData.id ?? '');
		localStorage.setItem('groupId', userData.groupId ?? '');
		localStorage.setItem('orgId', userData.organisationId ?? '');
		localStorage.setItem('firstName', userData.name ?? '');
		localStorage.setItem('username', userData.email ?? '');
		localStorage.setItem('userPic', userData.userPic ?? '');
		localStorage.setItem('roleId', userData.roleId ?? '');
		localStorage.setItem('mustChangePassword', userData.mustChangePassword === true ? 'true' : 'false');
	}

	saveDataInLocalStorage(userData: any) {
		this.setUserSession(userData);
	}
}
